import { useEffect, useRef } from 'preact/hooks';
import './odometer-theme.scss';
import './odometer.min.js';

// Simple type declaration for the Odometer global
declare global {
  interface Window {
    Odometer: any;
  }
}

interface OdometerProps {
  heading: string;
}

export const Odometer = ({ heading }: OdometerProps) => {
  const odometerInstances = useRef<Map<Element, any>>(new Map());
  const instanceId = useRef<string>(`odometer-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    // Clear any existing odometer instances on mount
    odometerInstances.current.forEach((odometer) => {
      if (odometer && odometer.destroy) {
        odometer.destroy();
      }
    });
    odometerInstances.current.clear();

    // Parse the heading to get odometer values
    const parser = new DOMParser();
    const doc = parser.parseFromString(heading, 'text/html');
    const odometerElement = doc.querySelector('.odometer');
    
    // If no odometer element is found, return
    if (!odometerElement) return;

    const startValue = odometerElement.getAttribute('data-odometer-start') || '';
    const endValue = odometerElement.textContent || '';

    // Convert values to numbers once
    const startNum = parseInt(startValue.replace(/,/g, ''), 10);
    const endNum = parseInt(endValue.replace(/,/g, ''), 10);

    if (window.Odometer) {
      // Initialize all odometers with their start values
      document.querySelectorAll('.odometer').forEach(el => {
        // Add unique identifier to the element
        el.setAttribute('data-odometer-id', instanceId.current);
        
        if (!odometerInstances.current.has(el)) {
          const odometer = new window.Odometer({
            el: el,
            value: startNum,
            format: '(,ddd)',
          });
          odometerInstances.current.set(el, odometer);
        }
      });

      // Function to check visibility and update values
      const checkVisibility = () => {
        document.querySelectorAll(`.odometer[data-odometer-id="${instanceId.current}"]`).forEach(el => {
          const odometer = odometerInstances.current.get(el);
          if (odometer) {
            const rect = el.getBoundingClientRect();
            const isVisible = (
              rect.top >= 0 &&
              rect.left >= 0 &&
              rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
              rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );

            if (isVisible && odometer.value !== endNum) {
              odometer.update(endNum);
            }
          }
        });
      };

      // Only debounce resize events
      let resizeTimeout: number;
      const debouncedResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(checkVisibility, 100);
      };

      // Check visibility immediately on scroll
      window.addEventListener('scroll', checkVisibility);
      window.addEventListener('resize', debouncedResize);
      
      // Initial check
      checkVisibility();

      return () => {
        window.removeEventListener('scroll', checkVisibility);
        window.removeEventListener('resize', debouncedResize);
        clearTimeout(resizeTimeout);
        
        // Clean up odometer instances on unmount
        odometerInstances.current.forEach((odometer) => {
          if (odometer && odometer.destroy) {
            odometer.destroy();
          }
        });
        odometerInstances.current.clear();
      };
    }
  }, [heading]);

  return null; // This component doesn't render anything
};